import type { OcrFoliarSample } from '@practium/features/api';
import { DatePicker, FormField, FormInput, FormSectionTitle, FormSelect } from '@practium/ui';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { RichTextarea } from '../../../../recommendations';
import {
	ANALYSIS_CULTURE_OPTIONS,
	BIENNIAL_OPTIONS,
	SEASON_OPTIONS,
	UNIT_OPTIONS_MACRO_FOLIAR,
	UNIT_OPTIONS_MICRO_FOLIAR,
} from '../../../constants/analyses.constants';
import { useOcrQueue } from '../../../contexts/OcrQueueContext';
import { useLocationOptions } from '../../../hooks/useLocationOptions';
import { useOcrFormSync } from '../../../hooks/useOcrFormSync';
import { useOcrImport } from '../../../hooks/useOcrImport';
import { usePlantAnalysisForm } from '../../../hooks/usePlantAnalysisForm';
import type { AnalysisPageMode, PlantAnalysisFormData, PlantAnalysisValueKey } from '../../../types/analyses.types';
import { mapOcrSampleToPlantForm } from '../../../utils';
import { formatAnalysisValue, formatIsoDate, resolveLabel } from '../../../utils/analysisFormatters';
import { FloraUploadZone } from '../../FloraUploadZone/FloraUploadZone';
import styles from '../AnalysisForm.module.css';
import { AnalysisValueInput } from '../AnalysisValueInput';
import { LocationFormSection } from '../LocationFormSection';
import { OcrCounter } from '../OcrCounter';

export interface PlantAnalysisFormRef {
	submit: () => Promise<void>;
	discardImport: () => void;
	isDirty: () => boolean;
}

interface PlantAnalysisFormProps {
	mode: AnalysisPageMode;
	initialData?: PlantAnalysisFormData;
	analysisId?: string;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
	onOcrBusyChange?: (busy: boolean) => void;
	ocrSample?: OcrFoliarSample;
	ocrFileName?: string;
	ocrSampleIndex?: number;
	ocrSampleTotal?: number;
}

export const PlantAnalysisForm = forwardRef<PlantAnalysisFormRef, PlantAnalysisFormProps>(
	function PlantAnalysisForm(
		{ mode, initialData, ocrSample, ocrFileName, ocrSampleIndex, ocrSampleTotal, analysisId, onSubmit, onLoadingChange, onOcrBusyChange },
		ref
	) {
		const { addFiles } = useOcrQueue();
		const appliedOcrSampleRef = useRef<OcrFoliarSample | undefined>(undefined);
		const { form, errors, isLoading, set, populate, handleSubmit, isDirty } = usePlantAnalysisForm({
			initialData,
			analysisId,
			onLoadingChange,
		});

		const ocr = useOcrImport<OcrFoliarSample>('foliar', !analysisId);
		useEffect(() => {
			if (ocrSample && !analysisId && appliedOcrSampleRef.current !== ocrSample) {
				appliedOcrSampleRef.current = ocrSample;
				populate(mapOcrSampleToPlantForm(form, ocrSample));
			}
		}, [analysisId, form, ocrSample, populate]);

		const { carryOverRef, handleOcrFileSelect, ocrLoadingMessage, showOcrCounter, currentOcrSample, totalOcrSamples, ocrProgress } =
			useOcrFormSync({
				ocr,
				form,
				populate,
				mapSampleToForm: mapOcrSampleToPlantForm,
				analysisId,
				isLoading,
				onLoadingChange,
				onOcrBusyChange,
			});

		const { properties, areas, propertyOptions, areaOptions, sectorOptions, selectedSectorNames } =
			useLocationOptions(form);

		useImperativeHandle(
			ref,
			() => ({
				submit: async () => {
					const willAdvance = ocr.currentIndex + 1 < ocr.totalSamples;
					const snapshot = form;
					const saved = await handleSubmit({ showSuccessToast: !willAdvance });
					if (!saved) return;
					if (willAdvance) {
						carryOverRef.current = snapshot;
						ocr.advance();
						return;
					}
					ocr.reset();
					onSubmit();
				},
				discardImport: () => ocr.reset(),
				isDirty,
			}),
			[form, handleSubmit, ocr, onSubmit, carryOverRef, isDirty]
		);

		const setUnit = (key: keyof PlantAnalysisFormData['units'], unit: string) =>
			set('units', { ...form.units, [key]: unit });

		if (mode === 'view') {
			return (
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Informações Gerais</FormSectionTitle>
						<div className={styles.viewGrid}>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Título da Análise</span>
								<span className={styles.viewValue}>{form.name || '—'}</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Região</span>
								<span className={styles.viewValue}>{form.region || '—'}</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Data da Coleta</span>
								<span className={styles.viewValue}>{formatIsoDate(form.sampleAt)}</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Propriedade</span>
								<span className={styles.viewValue}>
									{properties.find((p: any) => p._id === form.property)?.name || '—'}
								</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Área</span>
								<span className={styles.viewValue}>
									{areas.find((a: any) => (a._id ?? a.name) === form.area)?.name || '—'}
								</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Setor</span>
								<span className={styles.viewValue}>{selectedSectorNames || '—'}</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Cultura</span>
								<span className={styles.viewValue}>
									{resolveLabel(ANALYSIS_CULTURE_OPTIONS, form.culture) || '—'}
								</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Época/Estação</span>
								<span className={styles.viewValue}>
									{resolveLabel(SEASON_OPTIONS, form.season) || 'Nenhuma'}
								</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Bienalidade</span>
								<span className={styles.viewValue}>
									{resolveLabel(BIENNIAL_OPTIONS, form.biennial) || 'Não se aplica'}
								</span>
							</div>
						</div>
						{form.note && (
							<div className={styles.viewField} style={{ gridColumn: '1 / -1' }}>
								<span className={styles.viewLabel}>Nota Livre / Observações</span>
								<span className={styles.viewText} dangerouslySetInnerHTML={{ __html: form.note }} />
							</div>
						)}
					</div>

					<div className={styles.section}>
						<FormSectionTitle>Macronutrientes</FormSectionTitle>
						<div className={styles.viewGrid3}>
							{(
								[
									['N', 'N (Nitrogênio)'],
									['P', 'P (Fósforo)'],
									['K', 'K (Potássio)'],
									['Ca', 'Ca (Cálcio)'],
									['Mg', 'Mg (Magnésio)'],
									['S', 'S (Enxofre)'],
								] as [PlantAnalysisValueKey, string][]
							).map(([key, label]) => (
								<div key={key} className={styles.viewField}>
									<span className={styles.viewLabel}>{label}</span>
									<span className={styles.viewValue}>
										{form[key] ? `${formatAnalysisValue(form[key])} ${form.units[key]}` : '—'}
									</span>
								</div>
							))}
						</div>
					</div>

					<div className={styles.section}>
						<FormSectionTitle>Micronutrientes</FormSectionTitle>
						<div className={styles.viewGrid3}>
							{(
								[
									['B', 'B (Boro)'],
									['Cu', 'Cu (Cobre)'],
									['Fe', 'Fe (Ferro)'],
									['Mn', 'Mn (Manganês)'],
									['Zn', 'Zn (Zinco)'],
								] as [PlantAnalysisValueKey, string][]
							).map(([key, label]) => (
								<div key={key} className={styles.viewField}>
									<span className={styles.viewLabel}>{label}</span>
									<span className={styles.viewValue}>
										{form[key] ? `${formatAnalysisValue(form[key])} ${form.units[key]}` : '—'}
									</span>
								</div>
							))}
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.form}>
				<FloraUploadZone
					label="Clique ou arraste o laudo foliar (PDF/XLS)"
					onFileSelect={handleOcrFileSelect}
					onFilesSelect={(files) => addFiles(files, 'foliar')}
					disabled={ocr.isBusy}
					loading={ocr.isBusy}
					loadingMessage={ocrLoadingMessage}
					blocked={ocr.hasPendingImport}
				/>

				<div className={styles.section}>
					<FormSectionTitle>Informações Gerais</FormSectionTitle>
					<div className={styles.fields}>
						<FormField label="Título da Análise" required error={errors.name}>
							<FormInput
								placeholder="Insira o título da análise"
								value={form.name}
								onChange={(e) => set('name', e.target.value)}
								hasError={!!errors.name}
							/>
						</FormField>
						<div className={styles.row2}>
							<FormField label="Região (Opcional)" error={errors.region}>
								<FormInput
									placeholder="Ex.: Sul de Minas"
									value={form.region}
									onChange={(e) => set('region', e.target.value)}
									hasError={!!errors.region}
								/>
							</FormField>
							<FormField label="Data da Coleta" required error={errors.sampleAt}>
								<DatePicker
									value={form.sampleAt}
									onChange={(v) => set('sampleAt', v)}
									hasError={!!errors.sampleAt}
								/>
							</FormField>
						</div>
						<div className={styles.row3}>
							<FormField label="Cultura" required error={errors.culture}>
								<FormSelect
									value={form.culture}
									onChange={(v) => set('culture', v)}
									options={ANALYSIS_CULTURE_OPTIONS}
									placeholder="Selecione"
									hasError={!!errors.culture}
								/>
							</FormField>
							<FormField label="Época/Estação" error={errors.season}>
								<FormSelect
									clearable
									value={form.season}
									onChange={(v) => set('season', v)}
									options={SEASON_OPTIONS}
									placeholder="Selecione"
								/>
							</FormField>
							<FormField label="Bienalidade" error={errors.biennial}>
								<FormSelect
									clearable
									value={form.biennial}
									onChange={(v) => set('biennial', v)}
									options={BIENNIAL_OPTIONS}
									placeholder="Selecione"
								/>
							</FormField>
						</div>
						<LocationFormSection
							property={form.property}
							area={form.area}
							sector={form.sector}
							errors={errors}
							onPropertyChange={(v) => {
								set('property', v);
								set('area', '');
								set('sector', []);
							}}
							onAreaChange={(v) => {
								set('area', v);
								set('sector', []);
							}}
							onSectorChange={(v) => set('sector', v)}
							propertyOptions={propertyOptions}
							areaOptions={areaOptions}
							sectorOptions={sectorOptions}
						/>
						<FormField label="Nota Livre / Observações" error={errors.note}>
							<div className={styles.noteWrapper}>
								<RichTextarea
									value={form.note}
									onChange={(v) => set('note', v)}
									placeholder="Insira o conteúdo da nota"
									hasError={!!errors.note}
								/>
							</div>
						</FormField>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Macronutrientes</FormSectionTitle>
					<div className={styles.fields}>
						<div className={styles.row3}>
							{(['N', 'P', 'K'] as const).map((key) => (
								<FormField
									key={key}
									label={key === 'N' ? 'N (Nitrogênio)' : key === 'P' ? 'P (Fósforo)' : 'K (Potássio)'}
									required
									error={errors[key]}
								>
									<AnalysisValueInput
										value={form[key]}
										unit={form.units[key]}
										onValueChange={(value) => set(key, value)}
										onUnitChange={(unit) => setUnit(key, unit)}
										hasError={!!errors[key]}
										options={UNIT_OPTIONS_MACRO_FOLIAR}
									/>
								</FormField>
							))}
						</div>
						<div className={styles.row3}>
							{(['Ca', 'Mg', 'S'] as const).map((key) => (
								<FormField
									key={key}
									label={key === 'Ca' ? 'Ca (Cálcio)' : key === 'Mg' ? 'Mg (Magnésio)' : 'S (Enxofre)'}
									required
									error={errors[key]}
								>
									<AnalysisValueInput
										value={form[key]}
										unit={form.units[key]}
										onValueChange={(value) => set(key, value)}
										onUnitChange={(unit) => setUnit(key, unit)}
										hasError={!!errors[key]}
										options={UNIT_OPTIONS_MACRO_FOLIAR}
									/>
								</FormField>
							))}
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Micronutrientes</FormSectionTitle>
					<div className={styles.fields}>
						<div className={styles.row3}>
							<FormField label="B (Boro)" required error={errors.B}>
								<AnalysisValueInput
									step="0.01"
									value={form.B}
									unit={form.units.B}
									onValueChange={(value) => set('B', value)}
									onUnitChange={(unit) => setUnit('B', unit)}
									hasError={!!errors.B}
									options={UNIT_OPTIONS_MICRO_FOLIAR}
								/>
							</FormField>
							<FormField label="Cu (Cobre)" required error={errors.Cu}>
								<AnalysisValueInput
									step="0.01"
									value={form.Cu}
									unit={form.units.Cu}
									onValueChange={(value) => set('Cu', value)}
									onUnitChange={(unit) => setUnit('Cu', unit)}
									hasError={!!errors.Cu}
									options={UNIT_OPTIONS_MICRO_FOLIAR}
								/>
							</FormField>
							<FormField label="Fe (Ferro)" required error={errors.Fe}>
								<AnalysisValueInput
									value={form.Fe}
									unit={form.units.Fe}
									onValueChange={(value) => set('Fe', value)}
									onUnitChange={(unit) => setUnit('Fe', unit)}
									hasError={!!errors.Fe}
									options={UNIT_OPTIONS_MICRO_FOLIAR}
								/>
							</FormField>
						</div>
						<div className={styles.row2}>
							<FormField label="Mn (Manganês)" required error={errors.Mn}>
								<AnalysisValueInput
									value={form.Mn}
									unit={form.units.Mn}
									onValueChange={(value) => set('Mn', value)}
									onUnitChange={(unit) => setUnit('Mn', unit)}
									hasError={!!errors.Mn}
									options={UNIT_OPTIONS_MICRO_FOLIAR}
								/>
							</FormField>
							<FormField label="Zn (Zinco)" required error={errors.Zn}>
								<AnalysisValueInput
									value={form.Zn}
									unit={form.units.Zn}
									onValueChange={(value) => set('Zn', value)}
									onUnitChange={(unit) => setUnit('Zn', unit)}
									hasError={!!errors.Zn}
									options={UNIT_OPTIONS_MICRO_FOLIAR}
								/>
							</FormField>
						</div>
					</div>
				</div>

				{ocrSample && ocrSampleTotal ? (
					<OcrCounter
						current={(ocrSampleIndex ?? 0) + 1}
						total={ocrSampleTotal}
						progress={((ocrSampleIndex ?? 0) + 1) / ocrSampleTotal * 100}
						label={ocrFileName ? `Análises deste ${ocrFileName}` : 'Análises deste PDF'}
					/>
				) : showOcrCounter ? (
					<OcrCounter current={currentOcrSample} total={totalOcrSamples} progress={ocrProgress} />
				) : null}
			</div>
		);
	}
);
